import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Wrench, Package, Plus, Loader2, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { PartnerBadge } from '@/components/PartnerBadge';
import { formatReais, formatNumber } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useBalance } from '@/hooks/useBalance';
import { useCatalogList, type CatalogProduct } from '@/hooks/useCatalog';
import { useTier } from '@/hooks/useTier';
import { useDashboard } from '@/hooks/useDashboard';
import { CatalogProductModal } from '@/components/CatalogProductModal';
import { SERVICE_CATEGORIES, getServiceCategoryLabel } from '@mustauto/types';

// Vocabulário de contexto B2C (verticais financeiro/especialista) — define em
// quais alvos a oferta patrocinada aparece no app do cliente (E.3/E.4).
const OBLIGATION_TYPE_OPTIONS = [
  { value: 'ipva', label: 'IPVA' },
  { value: 'licensing', label: 'Licenciamento' },
  { value: 'insurance', label: 'Seguro' },
  { value: 'fines', label: 'Multas' },
];
const PROJECT_TYPE_OPTIONS = [
  { value: 'som', label: 'Som' },
  { value: 'visual', label: 'Visual' },
  { value: 'performance', label: 'Performance' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'conforto', label: 'Conforto' },
  { value: 'manutencao', label: 'Manutenção' },
];

const CATEGORIES = [
  'Pneus',
  'Freios',
  'Suspensão',
  'Motor',
  'Transmissão',
  'Elétrica',
  'Ar condicionado',
  'Funilaria',
  'Lubrificantes',
  'Filtros',
  'Baterias',
  'Acessórios',
];

// Fonte única em @mustauto/types (mesma lista que a API valida).
const SERVICE_CATEGORY_OPTIONS = SERVICE_CATEGORIES.map((c) => ({
  value: c.key,
  label: c.label,
}));

const SURFACES = [
  { value: 'mechanic_os' as const, label: 'OS do mecânico' },
  { value: 'b2c_quote' as const, label: 'Orçamento do cliente' },
  { value: 'b2c_app' as const, label: 'App B2C' },
];

const STATES = [
  'SP', 'RJ', 'MG', 'ES', 'PR', 'SC', 'RS', 'BA', 'PE', 'CE',
  'GO', 'DF', 'PA', 'MA', 'MT', 'MS', 'AM', 'PI', 'RN', 'PB',
  'AL', 'SE', 'TO', 'RO', 'AC', 'AP', 'RR',
];

const BUDGET_TIERS = [6000, 12000, 18000, 24000];

/** Map opportunity descriptions (from OS items) to campaign categories. */
const CATEGORY_KEYWORDS: [RegExp, string][] = [
  [/oleo|lubrificant/i, 'Lubrificantes'],
  [/filtro/i, 'Filtros'],
  [/pastilha|freio|disco de freio|fluido de freio/i, 'Freios'],
  [/pneu|alinhamento|balanceamento/i, 'Pneus'],
  [/amortecedor|suspens|mola|bieleta|terminal/i, 'Suspensão'],
  [/correia|vela|ignicao|motor|arrefecimento|bomba d.?agua|junta|radiador/i, 'Motor'],
  [/bateria/i, 'Baterias'],
  [/ar.?condicionado|higieniza/i, 'Ar condicionado'],
  [/eletri|sensor|alternador|lampada/i, 'Elétrica'],
  [/transmiss|cambio|embreagem/i, 'Transmissão'],
  [/funilaria|pintura|lanternagem/i, 'Funilaria'],
  [/palheta|acessorio/i, 'Acessórios'],
];

function mapToCategory(description: string): string {
  for (const [pattern, category] of CATEGORY_KEYWORDS) {
    if (pattern.test(description)) return category;
  }
  return '';
}

type CampaignType = 'parts' | 'service';

interface CampaignForm {
  campaignType: CampaignType;
  name: string;
  category: string;
  serviceCategory: string;
  regions: string[];
  surfaces: ('mechanic_os' | 'b2c_quote' | 'b2c_app' | 'mechanic_outsource')[];
  budget: number;
  impressionsGuarantee: number;
  displayMode?: 'catalog' | 'storefront';
  storefrontStoreName?: string;
  storefrontSearchUrlTemplate?: string;
  // Service-specific fields
  providerPhone?: string;
  providerRating?: number;
  coverageRadiusKm?: number;
  coverageType?: 'radius' | 'city' | 'state' | 'national';
  providerLogoUrl?: string;
  // Vertical financeiro/especialista (oferta patrocinada B2C)
  b2cTypes: string[];
  b2cCondition: string;
  b2cCtaLabel: string;
  b2cUrl: string;
}

export function CreateCampaignPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/app/campaigns/new' });
  const { isSurfaceAllowed } = useTier();
  const hasPreFill = !!(search.category || search.suggestedBudget);
  const [step, setStep] = useState(hasPreFill ? 1 : 0); // skip type selection when pre-filled
  const [form, setForm] = useState<CampaignForm>(() => {
    const regions: string[] = [];
    if (search.region) {
      const parts = search.region.split(',').map((s) => s.trim()).filter(Boolean);
      for (const p of parts) {
        if (!p.startsWith('+')) regions.push(p);
      }
    }
    const mappedCategory = search.category ? mapToCategory(search.category) : '';
    return {
      campaignType: 'parts',
      name: search.category ? `Campanha ${mappedCategory || search.category}` : '',
      category: mappedCategory || search.category || '',
      serviceCategory: '',
      regions,
      surfaces: ['mechanic_os', 'b2c_quote', 'b2c_app'],
      budget: search.suggestedBudget ?? 6000,
      impressionsGuarantee: 0,
      coverageType: 'national',
      b2cTypes: [],
      b2cCondition: '',
      b2cCtaLabel: '',
      b2cUrl: '',
    };
  });

  const { user } = useAuth();
  const { data: dashboardData } = useDashboard();
  const partnerVertical = dashboardData?.partner?.vertical ?? 'autopecas';
  const isB2cVertical = partnerVertical === 'financeiro' || partnerVertical === 'especialista';
  const b2cKind = partnerVertical === 'financeiro' ? 'obligation' : 'project';
  const b2cTypeOptions = partnerVertical === 'financeiro' ? OBLIGATION_TYPE_OPTIONS : PROJECT_TYPE_OPTIONS;

  // Verticais B2C servem exclusivamente na surface do app do cliente.
  useEffect(() => {
    if (isB2cVertical) {
      setForm((prev) =>
        prev.campaignType === 'parts' &&
        prev.surfaces.length === 1 &&
        prev.surfaces[0] === 'b2c_app'
          ? prev
          : { ...prev, campaignType: 'parts', surfaces: ['b2c_app'] },
      );
    }
  }, [isB2cVertical]);

  function toggleB2cType(type: string) {
    setForm((prev) => ({
      ...prev,
      b2cTypes: prev.b2cTypes.includes(type)
        ? prev.b2cTypes.filter((t) => t !== type)
        : [...prev.b2cTypes, type],
    }));
  }
  const pid = user?.partnerId ?? '';
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<Set<string>>(new Set());
  const [showCreateProduct, setShowCreateProduct] = useState(false);

  // Fetch catalog products for the selected category
  const { data: catalogData, isLoading: catalogLoading } = useCatalogList(pid, {
    category: form.category || undefined,
    status: 'active',
    limit: 50,
  });
  const catalogProducts = catalogData?.products ?? [];

  function toggleCatalogProduct(id: string) {
    setSelectedCatalogIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isService = form.campaignType === 'service';
  const totalSteps = isService ? 5 : 4;
  const stepLabels = isService
    ? ['Tipo', 'Serviço', 'Segmentação', 'Budget', 'Revisão']
    : ['Tipo', 'Produto', 'Segmentação', 'Budget', 'Revisão'];

  const createMutation = useMutation({
    mutationFn: async (data: CampaignForm) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        category: data.category || data.serviceCategory,
        regions: data.regions,
        budget: data.budget,
        impressionsGuarantee: data.impressionsGuarantee,
        campaignType: data.campaignType,
      };

      // Coverage type (applies to both parts and service campaigns)
      if (data.coverageType) {
        payload['coverageType'] = data.coverageType;
        if (data.coverageType === 'radius' && data.coverageRadiusKm) {
          payload['coverageRadiusKm'] = data.coverageRadiusKm;
        }
      }

      if (isB2cVertical) {
        // Oferta patrocinada B2C: surface fixa no app + contexto + oferta.
        payload['category'] = partnerVertical;
        payload['surfaces'] = ['b2c_app'];
        payload['b2cContext'] = { kind: b2cKind, types: data.b2cTypes };
        payload['b2cOffer'] = {
          conditionText: data.b2cCondition,
          ctaLabel: data.b2cCtaLabel,
          destinationUrl: data.b2cUrl,
        };
      } else if (data.campaignType === 'parts') {
        payload['surfaces'] = data.surfaces;
        payload['displayMode'] = data.displayMode;
      } else {
        payload['surfaces'] = ['mechanic_outsource'];
        payload['serviceCategory'] = data.serviceCategory;
        payload['providerPhone'] = data.providerPhone;
        payload['providerRating'] = data.providerRating;
        payload['providerLogoUrl'] = data.providerLogoUrl;
      }

      const res = await api.post('/partners/my/campaigns', payload);
      const campaignId = (res.data as { campaign: { id: string } }).campaign.id;

      if (data.displayMode === 'storefront' && data.storefrontStoreName && data.storefrontSearchUrlTemplate) {
        await api.put(`/partners/my/campaigns/${campaignId}/storefront`, {
          storeName: data.storefrontStoreName,
          searchUrlTemplate: data.storefrontSearchUrlTemplate,
        });
      }

      // Link selected catalog products
      if (data.displayMode === 'catalog' && selectedCatalogIds.size > 0 && pid) {
        await api.post(`/partners/${pid}/campaigns/${campaignId}/products/link`, {
          productIds: [...selectedCatalogIds],
        });
      }

      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['partner-campaigns'] });
      navigate({ to: '/campaigns' });
    },
  });

  function canAdvance(): boolean {
    switch (step) {
      case 0: return true; // campaign type always selected
      case 1: {
        if (isB2cVertical) {
          return (
            form.name.trim().length > 0 &&
            form.b2cTypes.length > 0 &&
            form.b2cCondition.trim().length > 0 &&
            form.b2cCtaLabel.trim().length > 0 &&
            /^https?:\/\//.test(form.b2cUrl.trim())
          );
        }
        if (isService) {
          return form.name.trim().length > 0 && form.serviceCategory.length > 0;
        }
        if (form.name.trim().length === 0 || form.category.length === 0) return false;
        if (form.displayMode === 'storefront') {
          return !!(form.storefrontStoreName?.trim() && form.storefrontSearchUrlTemplate?.includes('{query}'));
        }
        return true;
      }
      case 2: {
        if (isService) return true; // regions are optional, no surfaces to pick
        return form.surfaces.length > 0;
      }
      case 3: return form.budget > 0;
      case 4: return true; // review — service campaigns have extra step
      default: return false;
    }
  }

  function handleNext() {
    if (step < totalSteps) setStep(step + 1);
  }

  function handleBack() {
    if (step > 0) setStep(step - 1);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    createMutation.mutate(form);
  }

  function selectCampaignType(type: CampaignType) {
    setForm((prev) => ({
      ...prev,
      campaignType: type,
      surfaces: type === 'service' ? ['mechanic_outsource'] : ['mechanic_os', 'b2c_quote', 'b2c_app'],
      coverageType: type === 'service' ? 'radius' : 'national',
      coverageRadiusKm: type === 'service' ? 25 : undefined,
    }));
    setStep(1);
  }

  function toggleRegion(state: string) {
    setForm((prev) => ({
      ...prev,
      regions: prev.regions.includes(state)
        ? prev.regions.filter((r) => r !== state)
        : [...prev.regions, state],
    }));
  }

  function toggleSurface(surface: 'mechanic_os' | 'b2c_quote' | 'b2c_app') {
    setForm((prev) => ({
      ...prev,
      surfaces: prev.surfaces.includes(surface)
        ? prev.surfaces.filter((s) => s !== surface)
        : [...prev.surfaces, surface],
    }));
  }

  const estimatedImpressions = Math.round(form.budget * 10);
  const estimatedClicks = Math.round(estimatedImpressions * 0.03);
  const estimatedLeads = Math.round(estimatedClicks * 0.4);

  const serviceCategoryLabel = form.serviceCategory
    ? getServiceCategoryLabel(form.serviceCategory)
    : '';

  // Aviso (não trava): campanha pode ser criada e aguardar recarga.
  const { data: balanceData } = useBalance();
  const availableBalance = balanceData?.currentBalance ?? null;
  const insufficientBalance = availableBalance !== null && form.budget > availableBalance;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate({ to: '/campaigns' })}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} />
          Voltar
        </button>
        <h1 className="text-2xl font-bold">Nova campanha</h1>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2">
        {stepLabels.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                i <= step
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {i < step ? <Check size={14} /> : i}
            </div>
            <span className={`text-sm ${i <= step ? 'font-medium' : 'text-muted-foreground'}`}>
              {label}
            </span>
            {i < stepLabels.length - 1 && <div className="mx-1 h-px w-6 bg-border" />}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 0: Campaign Type */}
        {step === 0 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <label className="mb-2 block text-sm font-medium">
                Que tipo de campanha deseja criar?
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => selectCampaignType('parts')}
                  className={`rounded-lg border p-6 text-left transition-colors ${
                    form.campaignType === 'parts'
                      ? 'border-primary bg-accent'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <Package size={32} className="mb-3 text-primary" />
                  <div className="text-sm font-bold">Peças e produtos</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Anuncie peças, lubrificantes, pneus e acessórios nas ordens de serviço e app do cliente.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => selectCampaignType('service')}
                  className={`rounded-lg border p-6 text-left transition-colors ${
                    form.campaignType === 'service'
                      ? 'border-primary bg-accent'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <Wrench size={32} className="mb-3 text-primary" />
                  <div className="text-sm font-bold">Serviços especializados</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Anuncie retífica, funilaria, elétrica avançada e outros serviços na tela de terceirizados.
                  </p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1 (verticais B2C): oferta patrocinada */}
        {step === 1 && isB2cVertical && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome da campanha</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={partnerVertical === 'financeiro' ? 'Ex: Parcelamento de IPVA' : 'Ex: Especialista em som automotivo'}
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {partnerVertical === 'financeiro' ? 'Aparece nas obrigações' : 'Aparece nos projetos'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {b2cTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleB2cType(opt.value)}
                      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                        form.b2cTypes.includes(opt.value)
                          ? 'border-primary bg-accent text-primary font-medium'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Condição da oferta</label>
                <input
                  type="text"
                  value={form.b2cCondition}
                  onChange={(e) => setForm({ ...form, b2cCondition: e.target.value })}
                  placeholder="Ex: Parcele em até 12x sem juros"
                  maxLength={200}
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Texto do botão (CTA)</label>
                <input
                  type="text"
                  value={form.b2cCtaLabel}
                  onChange={(e) => setForm({ ...form, b2cCtaLabel: e.target.value })}
                  placeholder="Ex: Pagar agora"
                  maxLength={40}
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">URL de destino</label>
                <input
                  type="url"
                  value={form.b2cUrl}
                  onChange={(e) => setForm({ ...form, b2cUrl: e.target.value })}
                  placeholder="https://..."
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <p className="rounded-lg bg-accent p-3 text-xs text-muted-foreground">
                A oferta aparece com o selo <strong>[PARCEIRO]</strong> e o aviso de patrocínio no app do cliente (surface b2c_app, requer tier Ouro).
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Product/Service details */}
        {step === 1 && !isService && !isB2cVertical && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome da campanha</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Pneus Michelin - SP/RJ"
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Categoria</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setForm({ ...form, category: cat })}
                      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                        form.category === cat
                          ? 'border-primary bg-accent text-primary font-medium'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {form.category && (
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Como deseja exibir seus produtos?
                  </label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, displayMode: 'catalog' })}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        form.displayMode === 'catalog'
                          ? 'border-primary bg-accent'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold">Catálogo de produtos</span>
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-950/40 dark:text-green-300">
                          Maior conversão
                        </span>
                      </div>
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <li>+ Preço real e link direto</li>
                        <li>+ Foto do produto</li>
                        <li>+ Prazo de entrega</li>
                        <li>- Requer cadastro de produtos</li>
                      </ul>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, displayMode: 'storefront' })}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        form.displayMode === 'storefront'
                          ? 'border-primary bg-accent'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold">Link de vitrine</span>
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                          Mais simples
                        </span>
                      </div>
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <li>+ Configuração rápida</li>
                        <li>+ Busca automática na loja</li>
                        <li>- Sem preço exato</li>
                        <li>- Menor conversão</li>
                      </ul>
                    </button>
                  </div>
                </div>
              )}

              {form.displayMode === 'storefront' && (
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Nome da loja</label>
                    <input
                      type="text"
                      value={form.storefrontStoreName ?? ''}
                      onChange={(e) => setForm({ ...form, storefrontStoreName: e.target.value })}
                      placeholder="Ex: MinhaPecas.com.br"
                      className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      URL de busca (use {'{query}'} como placeholder)
                    </label>
                    <input
                      type="text"
                      value={form.storefrontSearchUrlTemplate ?? ''}
                      onChange={(e) => setForm({ ...form, storefrontSearchUrlTemplate: e.target.value })}
                      placeholder="https://loja.com/busca?q={query}"
                      className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                    />
                  </div>
                </div>
              )}

              {form.displayMode === 'catalog' && form.category && (
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Selecione produtos do catalogo
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCreateProduct(true)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      <Plus size={12} /> Criar novo produto
                    </button>
                  </div>

                  {catalogLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    </div>
                  ) : catalogProducts.length === 0 ? (
                    <div className="rounded-lg bg-muted p-4 text-center">
                      {/* Fica a mao de proposito: o texto interpola a categoria
                          escolhida no passo, e o `description` do EmptyState e
                          string, nao ReactNode. Trocar por um texto generico
                          perderia justamente o que orienta o parceiro aqui. */}
                      <Package size={24} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Nenhum produto na categoria "{form.category}".
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowCreateProduct(true)}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
                      >
                        <Plus size={12} /> Criar produto
                      </button>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {catalogProducts.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => toggleCatalogProduct(product.id)}
                          className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                            selectedCatalogIds.has(product.id)
                              ? 'border-primary bg-accent'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCatalogIds.has(product.id)}
                            readOnly
                            className="h-4 w-4 rounded border-border accent-primary"
                          />
                          {product.photoUrl ? (
                            <img src={product.photoUrl} alt={product.name} className="h-8 w-8 rounded object-cover" />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-xs">—</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{product.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {product.price ? formatReais(Number(product.price)) : '—'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCatalogIds.size > 0 && (
                    <div className="text-xs text-primary font-medium">
                      {selectedCatalogIds.size} produto(s) selecionado(s)
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {step === 1 && isService && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome da campanha</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Retifica Premium SP"
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Categoria do serviço</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {SERVICE_CATEGORY_OPTIONS.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setForm({ ...form, serviceCategory: cat.value })}
                      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                        form.serviceCategory === cat.value
                          ? 'border-primary bg-accent text-primary font-medium'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Segmentation */}
        {step === 2 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Regiões (deixe vazio para todas)
                </label>
                <div className="flex flex-wrap gap-2">
                  {STATES.map((state) => (
                    <button
                      key={state}
                      type="button"
                      onClick={() => toggleRegion(state)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                        form.regions.includes(state)
                          ? 'border-primary bg-accent text-primary'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>

              {isB2cVertical && (
                <div className="rounded-lg bg-accent p-4">
                  <p className="text-sm text-muted-foreground">
                    Esta campanha aparece no app do cliente, no contexto selecionado (surface: b2c_app).
                  </p>
                </div>
              )}

              {!isService && !isB2cVertical && (
                <div>
                  <label className="mb-1 block text-sm font-medium">Superfícies</label>
                  <div className="space-y-2">
                    {SURFACES.map((s) => {
                      const allowed = isSurfaceAllowed(s.value);
                      return (
                        <label
                          key={s.value}
                          className={`flex items-center gap-3 rounded-lg border border-border p-3 ${
                            allowed ? 'cursor-pointer hover:bg-muted' : 'cursor-not-allowed opacity-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.surfaces.includes(s.value)}
                            onChange={() => allowed && toggleSurface(s.value)}
                            disabled={!allowed}
                            className="h-4 w-4 rounded border-border accent-primary"
                          />
                          <span className="text-sm">{s.label}</span>
                          {!allowed && <Lock size={14} className="ml-auto text-muted-foreground" />}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {isService && (
                <div className="rounded-lg bg-accent p-4">
                  <p className="text-sm text-muted-foreground">
                    Campanhas de serviço aparecem automaticamente na tela de Terceirizados do mecânico (surface: mechanic_outsource).
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium">Cobertura geográfica</label>
                <div className="flex flex-wrap gap-2">
                  {([
                    { label: '3 km', type: 'radius' as const, radius: 3 },
                    { label: '5 km', type: 'radius' as const, radius: 5 },
                    { label: '10 km', type: 'radius' as const, radius: 10 },
                    { label: '25 km', type: 'radius' as const, radius: 25 },
                    { label: '50 km', type: 'radius' as const, radius: 50 },
                    { label: 'Cidade', type: 'city' as const, radius: undefined },
                    { label: 'Estado', type: 'state' as const, radius: undefined },
                    { label: 'Nacional', type: 'national' as const, radius: undefined },
                  ] as const).map((opt) => {
                    const isActive = form.coverageType === opt.type
                      && (opt.type !== 'radius' || form.coverageRadiusKm === opt.radius);
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          isActive
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-muted text-muted-foreground hover:border-primary/50'
                        }`}
                        onClick={() => setForm({
                          ...form,
                          coverageType: opt.type,
                          coverageRadiusKm: opt.radius,
                        })}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isService
                    ? 'Define até onde seu serviço atende a partir do endereço da empresa'
                    : 'Define a região onde seus produtos serão recomendados'
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Budget */}
        {step === 3 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <label className="mb-1 block text-sm font-medium">Orçamento mensal</label>
                <input
                  type="number"
                  min={100}
                  step={100}
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })}
                  className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-muted-foreground">
                  Comparativo de investimento
                </label>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {BUDGET_TIERS.map((tier) => {
                    const imp = tier * 10;
                    const clk = Math.round(imp * 0.03);
                    const lds = Math.round(clk * 0.4);
                    return (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => setForm({ ...form, budget: tier })}
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          form.budget === tier
                            ? 'border-primary bg-accent'
                            : 'border-border hover:bg-muted'
                        }`}
                      >
                        <div className="text-lg font-bold text-primary">
                          {formatReais(tier)}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          ~{formatNumber(imp)} impressões
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ~{formatNumber(clk)} cliques
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ~{formatNumber(lds)} pedidos
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg bg-accent p-4">
                <div className="text-sm font-medium text-primary">Projeção para seu orçamento</div>
                <div className="mt-2 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedImpressions)}</div>
                    <div className="text-xs text-muted-foreground">Impressões</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedClicks)}</div>
                    <div className="text-xs text-muted-foreground">Cliques</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedLeads)}</div>
                    <div className="text-xs text-muted-foreground">Pedidos est.</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Service provider profile (service only) OR Review (parts) */}
        {step === 4 && isService && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <h2 className="text-lg font-bold">Perfil do prestador</h2>
              <p className="text-sm text-muted-foreground">
                Essas informações aparecem no card da Tela 3.5 do mecânico.
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Telefone / WhatsApp</label>
                  <input
                    type="tel"
                    value={form.providerPhone ?? ''}
                    onChange={(e) => setForm({ ...form, providerPhone: e.target.value })}
                    placeholder="(11) 99999-9999"
                    className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Avaliação (1-5)</label>
                  <div className="flex gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setForm({ ...form, providerRating: form.providerRating === v ? undefined : v })}
                        className={`text-2xl ${v <= (form.providerRating ?? 0) ? 'text-amber-400 dark:text-amber-300' : 'text-gray-300 dark:text-gray-600'}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview card */}
              <div className="rounded-lg border border-dashed border-border p-4">
                <div className="text-xs font-medium text-muted-foreground mb-2">Preview do card:</div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{form.name || 'Nome da campanha'}</span>
                    <PartnerBadge />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {form.providerRating && (
                      <span className="text-xs text-amber-500 dark:text-amber-400">
                        {'★'.repeat(form.providerRating)}{'☆'.repeat(5 - form.providerRating)}
                      </span>
                    )}
                    {form.coverageType && (
                      <span className="text-xs text-muted-foreground">
                        {form.coverageType === 'radius' && form.coverageRadiusKm
                          ? `${form.coverageRadiusKm} km`
                          : form.coverageType === 'city' ? 'Cidade'
                          : form.coverageType === 'state' ? 'Estado'
                          : form.coverageType === 'national' ? 'Nacional'
                          : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {serviceCategoryLabel} · Patrocinado
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review — step 4 for parts, step 5 for service */}
        {((step === 4 && !isService) || (step === 5 && isService)) && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <h2 className="text-lg font-bold">Revisão da campanha</h2>

              <div className="space-y-3 rounded-lg bg-muted p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Tipo</span>
                  <span className="text-sm font-medium">
                    {isB2cVertical
                      ? (partnerVertical === 'financeiro' ? 'Oferta financeira' : 'Especialista')
                      : isService ? 'Serviço especializado' : 'Peças e produtos'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Nome</span>
                  <span className="text-sm font-medium">{form.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    {isB2cVertical ? 'Aparece em' : 'Categoria'}
                  </span>
                  <span className="text-sm font-medium">
                    {isB2cVertical
                      ? b2cTypeOptions.filter((o) => form.b2cTypes.includes(o.value)).map((o) => o.label).join(', ')
                      : isService ? serviceCategoryLabel : form.category}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Regiões</span>
                  <span className="text-sm font-medium">
                    {form.regions.length > 0 ? form.regions.join(', ') : 'Todas'}
                  </span>
                </div>
                {!isService && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Superfícies</span>
                    <span className="text-sm font-medium">
                      {form.surfaces
                        .map((s) => SURFACES.find((sf) => sf.value === s)?.label ?? s)
                        .join(', ')}
                    </span>
                  </div>
                )}
                {isService && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Superfície</span>
                    <span className="text-sm font-medium">Tela Terceirizados (mechanic_outsource)</span>
                  </div>
                )}
                {!isService && form.displayMode && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Modalidade</span>
                    <span className="text-sm font-medium">
                      {form.displayMode === 'catalog' ? 'Catálogo de produtos' : 'Link de vitrine'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Orçamento mensal</span>
                  <span className="text-sm font-bold text-primary">
                    {formatReais(form.budget)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-accent p-4">
                <div className="text-sm font-medium text-primary">Projeção estimada</div>
                <div className="mt-2 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedImpressions)}</div>
                    <div className="text-xs text-muted-foreground">Impressões</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedClicks)}</div>
                    <div className="text-xs text-muted-foreground">Cliques</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold">{formatNumber(estimatedLeads)}</div>
                    <div className="text-xs text-muted-foreground">Pedidos est.</div>
                  </div>
                </div>
              </div>

              {insufficientBalance && availableBalance !== null && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-950/30">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Saldo insuficiente: seu saldo é {formatReais(availableBalance)} e o orçamento é {formatReais(form.budget)}.
                  </p>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    Você pode criar a campanha mesmo assim — ela aguarda recarga para começar a rodar.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/balance/top-up' })}
                    className="mt-3 inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    Adicionar saldo
                    <ArrowRight size={12} />
                  </button>
                </div>
              )}

              {createMutation.isError && (
                <p className="text-sm text-destructive">
                  Erro ao criar campanha. Tente novamente.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
          >
            <ArrowLeft size={14} />
            Voltar
          </button>

          {step < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance()}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
            >
              Próximo
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
            >
              {createMutation.isPending ? 'Criando...' : 'Criar campanha'}
              <Check size={14} />
            </button>
          )}
        </div>
      </form>

      {showCreateProduct && (
        <CatalogProductModal
          onClose={() => setShowCreateProduct(false)}
          onCreated={(id) => {
            setSelectedCatalogIds((prev) => new Set([...prev, id]));
            setShowCreateProduct(false);
          }}
        />
      )}
    </div>
  );
}
